/**
 * iter413as — Guided tour for demo users (driver.js)
 *
 * 1) Auto-runs on first dashboard visit per session (tracked by localStorage).
 * 2) Manual "Spustit prohlídku znovu" trigger via global event `demo-tour:restart`.
 * 3) Module-specific micro-tours when user enters Customers / Quotes / Warehouse
 *    for the first time.
 *
 * Tour reads `data-testid` selectors that already exist in the codebase and
 * filters out steps whose element is not currently in the DOM, so the same
 * step list works across layouts (sidebar / topnav / mobile).
 */
import { useEffect, useRef } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { API_V1 } from "../config/api";

const KEY_MAIN = "demo_tour_done_v2";
const KEY_MODULE = (slug) => `demo_tour_module_${slug}_v2`;

// ─── Telemetry ──────────────────────────────────────────────────────────
// Best-effort fire-and-forget. Never throws, never blocks UI.
function logEvent(event, extra = {}) {
  try {
    axios.post(
      `${API_V1}/demo/telemetry`,
      { event, ...extra },
      { withCredentials: true, timeout: 5000 },
    ).catch(() => {});
  } catch (e) { /* ignore */ }
}

const COMMON_DRIVER_OPTS = {
  showProgress: true,
  animate: true,
  smoothScroll: true,
  allowClose: true,
  overlayColor: "rgba(2, 6, 23, 0.72)",
  stagePadding: 6,
  stageRadius: 12,
  progressText: "{{current}} z {{total}}",
  nextBtnText: "Další →",
  prevBtnText: "← Zpět",
  doneBtnText: "Začít používat",
};

// ─── MAIN ONBOARDING TOUR ────────────────────────────────────────────────
const MAIN_STEPS = [
  {
    element: "[data-testid='demo-mode-banner']",
    popover: {
      title: "👋 Vítej v Solar Green CRM!",
      description:
        "Toto je tvůj <strong>izolovaný sandbox</strong>. Všechny zde viditelné data jsou demo. Tvá data nikdo nevidí — ani Solar Green. Demo platí <strong>24 hodin</strong>.",
      side: "bottom", align: "center",
    },
  },
  {
    element: "[data-testid='welcome-dashboard'], main",
    popover: {
      title: "📊 Tvůj Dashboard",
      description:
        "Tady máš okamžitý přehled — poptávky, zákazníci, obrat a konverzní funnel nabídek.<br/><br/>Widgety si můžeš libovolně přesouvat nebo skrýt.",
      side: "bottom",
    },
  },
  {
    element: "[data-testid='approval-funnel-widget']",
    popover: {
      title: "📈 Konverzní funnel",
      description:
        "Sleduj kolik nabídek je: <strong>posláno → otevřeno → schváleno</strong>.<br/><br/>Zelené tlačítko <strong>Připomenout</strong> pošle WhatsApp/SMS zprávu zákazníkovi s QR linkem pro mobilní podpis.",
      side: "left",
    },
  },
  {
    element: "aside, [data-testid='sidebar-search-btn']",
    popover: {
      title: "📂 Levé menu — moduly",
      description:
        "Vidíš jen <strong>moduly povolené pro demo</strong> (Zákazníci, Nabídky, Faktury, Sklad, Smlouvy, Úkoly, Dokumenty, Kalendář).<br/><br/>Systémové sekce (Nastavení, Uživatelé, WhatsApp tokeny) jsou v demo skryté.",
      side: "right",
    },
  },
  {
    element: "[data-testid='nav-zákazníci'], [data-testid='fav-nav-customers']",
    popover: {
      title: "👥 Zákazníci (Kanban)",
      description:
        "Drag-and-drop kanban se 360° pohledem — historie, nabídky, faktury, smlouvy, e-maily, hovory, AI obohacení.",
      side: "right",
    },
  },
  {
    element: "[data-testid='nav-nabídky'], [data-testid='fav-nav-quotes']",
    popover: {
      title: "💰 Nabídky",
      description:
        "Multi-step wizard s katalogem položek, ROI panelem, PDF/HTML export. <strong>Public link + QR podpis</strong> vyrobí elektronicky podepsanou smlouvu automaticky.",
      side: "right",
    },
  },
  {
    element: "[data-testid='nav-sklad'], [data-testid='fav-nav-warehouse']",
    popover: {
      title: "📦 Sklad (WMS)",
      description:
        "QR etikety, audit skenováním, low-stock alerty, bulk import CSV, multi-warehouse view, A4 print labely.",
      side: "right",
    },
  },
  {
    popover: {
      title: "🚀 Hotovo, jdeme na to!",
      description:
        "Teď je řada na tobě:<br/><ul style='margin-top:8px;padding-left:18px;line-height:1.7'>" +
        "<li>👆 Klikni na zákazníka → 360° historii</li>" +
        "<li>📄 V Nabídkách otevři <strong>Veřejný link</strong> a vyzkoušej QR podpis</li>" +
        "<li>📦 Ve Skladu spusť <strong>Quick Audit</strong> pro inventuru</li>" +
        "<li>🔄 Prohlídku můžeš kdykoli spustit znovu z horního banneru</li>" +
        "</ul>",
      side: "over",
    },
  },
];

// ─── MODULE-SPECIFIC MICRO-TOURS ─────────────────────────────────────────
const MODULE_TOURS = {
  customers: {
    pathTest: (p) => /\/admin\/customers(\/|$|\?)/.test(p),
    steps: [
      {
        element: "[data-testid='customers-kanban'], main",
        popover: {
          title: "👥 Kanban zákazníků",
          description: "Drag-and-drop mezi stavy. Klikni na kartu pro 360° detail.",
          side: "bottom",
        },
      },
      {
        element: "[data-testid='customer-search'], input[type='search'], main",
        popover: {
          title: "🔍 Rychlé hledání",
          description: "Hledej podle jména, e-mailu, telefonu, IČO i tagů.",
          side: "bottom",
        },
      },
    ],
  },
  quotes: {
    pathTest: (p) => /\/admin\/quotes(\/|$|\?)/.test(p),
    steps: [
      {
        element: "main",
        popover: {
          title: "💰 Nabídky",
          description:
            "Klikni na <strong>+ Nová nabídka</strong> a projdi wizardem. " +
            "Po dokončení uvidíš public link s QR — to je celý vlastnoruční podpis 21. století.",
          side: "bottom",
        },
      },
    ],
  },
  warehouse: {
    pathTest: (p) => /\/admin\/warehouse(\/|$|\?)/.test(p),
    steps: [
      {
        element: "main",
        popover: {
          title: "📦 WMS — Sklad",
          description:
            "Skenovací inventura, QR etikety, multi-warehouse pohyby. " +
            "<br/><strong>Tip:</strong> Quick Audit = bleskové počítání položek mobilem.",
          side: "bottom",
        },
      },
    ],
  },
};

// ─── PUBLIC RESTART API ──────────────────────────────────────────────────
// Other components (e.g. DemoBanner button) dispatch:
//   window.dispatchEvent(new CustomEvent("demo-tour:restart"))
// to re-run the main tour.

function runTour(steps, opts = {}) {
  const visible = steps.filter((s) => !s.element || document.querySelector(s.element));
  if (visible.length === 0) return;
  const tourId = opts.tourId || "main";
  const d = driver({
    ...COMMON_DRIVER_OPTS,
    ...opts,
    steps: visible,
    onHighlightStarted: (el, step, ctx) => {
      logEvent("step_view", {
        step_index: ctx.state.activeIndex,
        step_title: step?.popover?.title || "",
        tour_id: tourId,
      });
    },
    onCloseClick: (el, step, ctx) => {
      logEvent("step_close", {
        step_index: ctx.state.activeIndex,
        step_title: step?.popover?.title || "",
        tour_id: tourId,
      });
      ctx.driver.destroy();
    },
    onDestroyed: (el, step, ctx) => {
      // Fired when tour ends (any way). If user reached the last visible step
      // and clicked "done", treat as completed; otherwise as abandoned.
      const total = visible.length;
      const idx = ctx?.state?.activeIndex ?? 0;
      const completed = idx >= total - 1;
      logEvent(completed ? "tour_completed" : "tour_abandoned", {
        step_index: idx,
        step_title: step?.popover?.title || "",
        tour_id: tourId,
        meta: { total_steps: total },
      });
      if (typeof opts.onAfter === "function") {
        try { opts.onAfter({ completed, idx, total }); } catch (e) { /* */ }
      }
    },
  });
  d.drive();
  return d;
}

export default function DemoGuidedTour() {
  const { user } = useAuth();
  const location = useLocation();
  const lastRunRef = useRef(null);

  // ─── Auto-run on first dashboard visit ────────────────────────────────
  useEffect(() => {
    if (user?.role !== "demo") return;
    if (localStorage.getItem(KEY_MAIN) === "1") return;

    const isDash =
      location.pathname.includes("/admin/dashboard") || location.pathname === "/admin";
    if (!isDash) return;

    const t = setTimeout(() => {
      logEvent("tour_started", { tour_id: "main" });
      runTour(MAIN_STEPS, {
        tourId: "main",
        onAfter: () => localStorage.setItem(KEY_MAIN, "1"),
      });
    }, 1500);
    return () => clearTimeout(t);
  }, [user, location.pathname]);

  // ─── Module micro-tours (once per module per session) ────────────────
  useEffect(() => {
    if (user?.role !== "demo") return;
    // Don't fire while main onboarding hasn't run yet
    if (localStorage.getItem(KEY_MAIN) !== "1") return;

    for (const [slug, conf] of Object.entries(MODULE_TOURS)) {
      if (!conf.pathTest(location.pathname)) continue;
      if (localStorage.getItem(KEY_MODULE(slug)) === "1") return;
      if (lastRunRef.current === slug) return;
      lastRunRef.current = slug;

      const t = setTimeout(() => {
        logEvent("tour_started", { tour_id: `module_${slug}` });
        runTour(conf.steps, {
          tourId: `module_${slug}`,
          onAfter: () => localStorage.setItem(KEY_MODULE(slug), "1"),
        });
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [user, location.pathname]);

  // ─── Manual restart via global event ─────────────────────────────────
  useEffect(() => {
    if (user?.role !== "demo") return;
    const handler = () => {
      try {
        localStorage.removeItem(KEY_MAIN);
        Object.keys(MODULE_TOURS).forEach((s) => localStorage.removeItem(KEY_MODULE(s)));
      } catch (e) { /* ignore */ }
      logEvent("tour_restarted_manually", { tour_id: "main" });
      runTour(MAIN_STEPS, {
        tourId: "main",
        onAfter: () => localStorage.setItem(KEY_MAIN, "1"),
      });
    };
    window.addEventListener("demo-tour:restart", handler);
    return () => window.removeEventListener("demo-tour:restart", handler);
  }, [user]);

  return null;
}
